import { useLocation } from 'react-router-dom';
import { Box, Modal, Svg } from '@hopskipdrive/react-components';
import { InvoiceGroup } from '@rideiq/organizers/features/InvoiceGroups/types';
import { useDeleteInvoiceGroup } from '@rideiq/organizers/pages/BillingInvoiceGroups';
import { ButtonLink, RouterLink } from '@rideiq/shared/components';
import { Button } from '@rideiq/shared/components/Button';
import { Text } from '@rideiq/shared/components/Text';
import { colorTokens } from '@rideiq/shared/components/variables';
import styled from 'styled-components';

const CloseButton = styled(RouterLink)`
  display: inline-flex;
  margin-left: auto;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: #ddebfa;
    svg {
      color: ${colorTokens.text.hover};
    }
  }

  &:focus-visible {
    outline: 2px solid ${colorTokens.primary.main};
    outline-offset: 2px;
  }
`;

const InfoRow = ({ label, value }: { label: string; value?: string }) => (
  <Box display="flex" gap="24px" alignItems="center">
    <Box width="150px" flexShrink={0}>
      <Text textColor={colorTokens.text.medium} style={{ fontSize: '13px' }}>
        {label}
      </Text>
    </Box>
    <Text>{value}</Text>
  </Box>
);

export const DeleteInvoiceGroupModal = () => {
  const { isDeleting, handleDelete } = useDeleteInvoiceGroup();
  const { state } = useLocation();
  const invoiceGroup = state?.invoiceGroup as InvoiceGroup | undefined;

  return (
    <Modal open={true} data-testid="delete-invoice-group-modal">
      <Modal.Title>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Text variant="h1" fontWeight={700}>
            Delete Invoice Group
          </Text>
          <CloseButton to="/settings/invoice_groups" aria-label="Close" type="button">
            <Svg height="20px" icon="close-outlined" />
          </CloseButton>
        </Box>
      </Modal.Title>
      <Modal.Content>
        <Box display="flex" flexDirection="column" gap="20px">
          <Text>
            Deleting this invoice group will move all assigned riders to the Default invoice group. This action cannot
            be undone.
          </Text>
          <Box display="flex" flexDirection="column" gap="12px">
            <InfoRow label="Invoice Group Name" value={invoiceGroup?.name} />
            <InfoRow label="Purchase Order Number" value={invoiceGroup?.poNumber} />
          </Box>
        </Box>
      </Modal.Content>
      <Modal.Actions>
        <ButtonLink color="tertiary" to="/settings/invoice_groups" disabled={isDeleting}>
          Never mind
        </ButtonLink>
        <Button
          color="primary"
          loading={isDeleting}
          disabled={!invoiceGroup}
          onClick={() => invoiceGroup && handleDelete(invoiceGroup.id)}
          sx={{ height: '36px', minWidth: '90px' }}
        >
          Delete and move riders
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
